export enum QuoteType {
  Budget = 0,
  Internal = 1,
  Distributor = 2,
}

export enum GenerationType {
  Normal,
  Auto,
  Economic,
}

export enum HeightType {
  Ffl,
  Cu,
}

export enum BaySpacing {
  Economic,
  Even,
  Uneven,
}

export enum BeamDirection {
  Auto,
  Horizontal,
  Vertical,
}

export enum BuildingStandard {
  British,
  Ec3,
}

export enum LoadType {
  Office,
  Storage,
  Ind,
  Medium,
  Heavy,
  Special,
}

export enum DrawingType {
  Quote,
  Project,
}

export class BayDistance {
  Auto: boolean;
  Value: number;

  constructor(auto: boolean, value: number) {
    this.Auto = auto;
    this.Value = value;
  }
}

export class SessionData {
  Username: string;
  SessionId: string;
}

export class QuoteReference {
  Quote: string;
  Revision: string;
  Option: string;
}

export class GeneralData {
  QuoteReference = new QuoteReference();
  QuoteType: QuoteType;
  DistributorName: string;
  ContactName: string;
  Reference: string;
  Notes: string;
  QuoteDate: string;
  /** Empty until an estimator sets one — the date picker clears to null. */
  ExpectedInstallationDate: string | null;
  BuildingStandard: BuildingStandard;
  IsFireRated: boolean;
  CostTransport: boolean;
  AreaCode: number | any;
  CostInstallation: boolean;
  CostAncillaries: boolean;
  ExWorks: boolean;
  CostInstallationAsExtraOver: boolean;
  /** Whether to display ancillaries on the 2D renderer. Default: false */
  ShowAncillaries?: boolean;
  /** Whether to allow staircases to be positioned inside the floor with auto-cutout. Default: false */
  AllowStaircaseInsideFloor?: boolean;
}

export class GpData {
  TargetGp: number;
  FireRatingGp: number;
}

export class GridData {
  BaySpacing: BaySpacing;
  BeamDirection: BeamDirection;
  NotifyIfOtherBeamDirectionCheaper: boolean;
  YDim: number;
  XDim: number;
  XBays: number;
  YBays: number;
  XBayDistances: BayDistance[];
  YBayDistances: BayDistance[];
  IsMinBasePlateWidthMainAuto: boolean;
  IsMinBasePlateWidthEdgeAuto: boolean;
  MinBasePlateWidthMain: number;
  MinBasePlateWidthEdge: number;
}

export enum BracingType {
  Auto = 0,
  Strut = 1,
  Cross = 2,
  Portal = 3,
}

export class BraceArgument {
  TierNumber: number;
  Type: BracingType;
  Orientation: number; // 1 = Horizontal, 2 = Vertical
  StartX: number; // metres
  StartY: number;
  EndX: number;
  EndY: number;
}

export class BraceData {
  BraceArguments: BraceArgument[];
}

export class DesignData {
  UcColumnsOnly: boolean;
  UseStubJoists: boolean;
  MaxFloorDepth: number;
  UseAngles: boolean;
  AdaptiveDesignProcedures: boolean;
  LimitStrutSections: boolean;
  StrutDistance: number;
  BracingType: BracingType;
}

export class DeckingData {
  IsDeckingRequired: boolean;
  DeckingCentres: number;
  DeckingSection: number | any;
}

export class CutoutData {
  Hash: string;
  X: number;
  Y: number;
  XDim: number;
  YDim: number;
  /** Whether changes to this cutout should trigger edge protection recalculation. Default: true */
  RecalculateEdgeProtection?: boolean;
}

export type EdgeType = 'LEFT' | 'RIGHT' | 'TOP' | 'BOTTOM';

export type SnappedEdgeType =
  EdgeType | 'CUTOUT_LEFT' | 'CUTOUT_RIGHT' | 'CUTOUT_TOP' | 'CUTOUT_BOTTOM';

export type DirectionType = 'HORIZONTAL' | 'VERTICAL';

export type DescentDirectionType = 'AWAY' | 'LEFT' | 'RIGHT';
export type StaircaseTurnDirectionType =
  'STRAIGHT' | 'LEFT' | 'RIGHT' | 'UTURN_LEFT' | 'UTURN_RIGHT';

export class EdgeProtectionPost {
  X: number; // Position X (metres)
  Y: number; // Position Y (metres)
}

export class EdgeProtectionRun {
  TierNumber: number; // Which tier (0-indexed)
  Edge: EdgeType; // Which edge of the floor
  StartX: number; // Start position X (metres)
  StartY: number; // Start position Y (metres)
  EndX: number; // End position X (metres)
  EndY: number; // End position Y (metres)
  Length: number; // Calculated length (metres)
  // Where the posts stand. Laid out across a whole tier, so a run may carry no
  // post on a corner it shares with its neighbour. Prime builds what these say;
  // runs saved before iQD sent them leave it undefined.
  Posts?: EdgeProtectionPost[];
}

export class TierData {
  Area: number;
  Depth: number;
  Height: number;
  LoadType: number | any;
  LiveLoad: number;
  DeadLoad: number;
  ServiceLoad: number;
  BeamDeflection: number;
  CalculatedBeamDeflections: number[];
  JoistDeflection: number;
  MinJoistThickness: number;
  CutoutData: CutoutData[];
}

export class TiersData {
  HeightType: HeightType;
  TierData: TierData[];
  CopyTierData: boolean;
  TierCount: number;
}

export class FloorData {
  GridData: GridData;
  DesignData: DesignData;
  DeckingData: DeckingData;
  TiersData: TiersData;
  BraceData: BraceData;
}

export class TransportData {
  TransportAuto: boolean;
  TransportLorries: number;
  AreaBandCost: number;
  LoadLorryCost: number;
  TransportTotal: number;
}

export class InstallationRate {
  installationRateType: InstallationRateType;
  days: number;
  area: number;
  netting: number;
}

export class InstallationRateType {
  description: string;
  costPerWeek: number;
}

export class InstallationData {
  IsInstallationAuto: boolean;
  ClearBuildJob: boolean;
  Area: number;
  TeamSize: string;
  NormalTeamCost: number;
  LargeTeamCost: number;
  Days: number;
  Cost: number;
  Netting: number;
  TotalCost: number;
  Stairs: number;
  Handrail: number;
  Locked: boolean;
  SystemDisabledAutoInstallation: boolean;
}

export class FireRatingData {
  CostFireRating: boolean;
  CostColumnCasings: boolean;
  ColumnCasingFinish: number; // enum?
  CostCeilings: boolean;
  CeilingGridSize: number;
  CostFascia: boolean;
  FasciaLengthAuto: boolean;
  FasciaLength: number;
  OverrideMinColumnThickness: boolean;
}

export interface Ancillary {
  id: number;
  shortDescription: string;
  longDescription: string;
  favourite: boolean;
  distributorEnabled: boolean;
  unitCost: number;
  width: number | null;
  depth: number | null;
  height: number | null;
  treadWidth: number | null;
  treadType: 'GeneralAccess' | 'Utility' | null;
  modelKey: string | null;
  postCentres: number | null;
  isRenderable: boolean;
  ancillaryType: {
    id: number;
    name: string;
  };
  ancillaryUnit: {
    id: number;
    name: string;
  };
}

/** Position data for a single placed ancillary instance */
export class AncillaryPosition {
  TierNumber: number; // Which tier (0-indexed)
  X: number; // X position in meters
  Y: number; // Y position in meters
  Width?: number; // Width in meters (staircases, gates)
  Depth?: number; // Depth in meters (staircases, gates)
  EndX?: number; // End X position (handrails)
  EndY?: number; // End Y position (handrails)
  Direction?: DirectionType;
  SnappedEdge?: SnappedEdgeType;
  DescentDirection?: DescentDirectionType;
  TurnDirection?: StaircaseTurnDirectionType;
  LandingDepth?: number;
}

export class AncillaryArgument {
  Ancillary: number;
  IsExtraOver: boolean;
  Height: number;
  Length: number;
  Quantity: number;
  IsPlant: boolean;
  IsAutoIncluded?: boolean; // Automatically included based on floor configuration (e.g. side handrail for multi-tier/tall floors)
  AutoCalculateHeight: boolean;

  // Positions for renderable ancillaries (one entry per placed instance)
  Positions?: AncillaryPosition[];

  // Edge protection auto-calculation fields
  AutoCalculateLength?: boolean; // Auto-calculate length based on floor perimeter
  RecalculateOnCutoutChange?: boolean; // Whether to recalculate runs when cutouts change. Default: true
  Runs?: EdgeProtectionRun[]; // Multiple runs for edge protection (one per continuous segment)

  // Dimensions (defaulted from the ancillary definition; user-overridable)
  Width?: number; // Width in metres
  Depth?: number; // Depth in metres
  PostCentres?: number; // Distance between posts in metres
  IsRenderable?: boolean; // Whether this ancillary can be displayed on 2D/3D model
  AncillaryType?: string; // Ancillary type name (e.g. 'Staircase', 'Gate', 'Edge Protection')
}

export class AncillaryData {
  CalcPlant: boolean;
  LongestJoistLength: number;
  LongestBeamLength: number;
  AncillaryArguments: AncillaryArgument[];
  OverrideNoGeneralAccessStaircase: boolean;
  OverrideNoEdgeProtection: boolean;
}

export class TransformData {
  ComponentId: string;
  TransformType: string;
  TierNumber: number;
  XPosition: number;
  YPosition: number;
  Index: number;
}

export class UserSettings {
  UserDefinedInstallation: boolean;
}

export class QuoteData {
  GeneralData: GeneralData;
  GpData: GpData;
  FloorData: FloorData;
  TransportData: TransportData;
  InstallationData: InstallationData;
  FireRatingData: FireRatingData;
  AncillaryData: AncillaryData;
  TransformData: TransformData[];
  UserSettings: UserSettings;
}
